// lib/api/ai-comments.ts
// OpenAI API를 사용한 AI 코멘트 및 추천 생성

export interface AnalysisScores {
  pitchAccuracy: number;
  rhythmStability: number;
  energyDynamics: number;
  emotionalExpression: number;
  toneConsistency: number;
  overallScore: number;
  tempo: number;
  duration: number;
  analysisType?: "vocal" | "instrument" | "composition";
}

export interface AIComments {
  pitch: string;
  rhythm: string;
  energy: string;
  emotion: string;
  tone: string;
  summary: string;
  coachComment?: string; // 감성적 AI 코치 코멘트
  nextStep?: string; // 다음 단계 추천
}

export interface PracticeRecommendation {
  title: string;
  description: string;
  link?: string;
  practiceSongs?: string[];
}

export async function generateAIComments(
  scores: AnalysisScores
): Promise<AIComments> {
  try {
    // 테스트 기간: 코멘트 생성은 Claude만 사용
    return await generateClaudeComments(scores);

    // (필요하면 아래 OpenAI 경로로 다시 전환)
    /*
    const provider = process.env.AI_COMMENTS_PROVIDER || "openai";
    if (provider === "claude") {
      return await generateClaudeComments(scores);
    }

    const apiKey = process.env.OPENAI_API_KEY || process.env.OPEN_API_KEY;

    if (!apiKey) {
      console.warn(
        "⚠️ OpenAI API 키가 없습니다. 기본 코멘트를 사용합니다. (OPENAI_API_KEY 또는 OPEN_API_KEY 필요)"
      );
      return generateDefaultComments(scores);
    }

    const typeLabel =
      scores.analysisType === "vocal"
        ? "보컬"
        : scores.analysisType === "instrument"
          ? "연주"
          : "작곡";

    const prompt = `당신은 20년 경력의 친절한 보컬/음악 코치입니다. 아래 ${typeLabel} 분석 결과를 바탕으로, 사용자가 상처받지 않으면서도 "다음에 뭘 연습해야 할지"가 명확해지도록 피드백을 작성해주세요.

중요:
- 자신이 AI라는 언급, "저는 ~입니다", "ChatGPT", "모델" 같은 표현은 절대 쓰지 마세요.
- 말투는 실제 보컬 트레이너가 옆에서 말하듯 자연스럽고 단호한 존댓말.
- 각 항목은 2~4문장 정도로 짧고 구체적으로.
- 마지막에는 항상 "그래서 다음에 뭐 해보면 좋을지"가 떠오르게 만드는 한 문장을 붙이세요.

분석 결과 (${typeLabel}):
- 음정 정확도: ${scores.pitchAccuracy}/100
- 리듬 안정성: ${scores.rhythmStability}/100
- 에너지 밸런스: ${scores.energyDynamics}/100
- 감정 표현력: ${scores.emotionalExpression}/100
- 톤 일관성: ${scores.toneConsistency}/100
- 템포: ${scores.tempo} BPM
- 길이: ${Math.round(scores.duration)}초

각 항목에 대해 아래 원칙을 지켜서 작성하세요:
1. 현재 상태를 짧게 설명 (1문장)
2. 강점 1개와 개선 포인트 1개를 짚기 (각 1문장)
3. 바로 연습에 써먹을 수 있는 액션을 제안 (1문장)
4. 과한 감탄사/비유/이모지는 쓰지 말 것

${typeLabel === "보컬" ? "보컬리스트" : typeLabel === "연주" ? "연주자" : "작곡가"}의 관점에서 전문적이면서도 차분한 톤으로 작성해주세요.

추가로, 다음 항목도 작성해주세요:
- coachComment: 따뜻하지만 과하지 않은 코멘트 (3~4문장, 실력과 가능성을 구체적으로 짚어주기)
- nextStep: 다음 단계로 추천하는 연습 방향 (1~2문장, 구체적인 행동 제안)

JSON 형식으로만 응답:
{
  "pitch": "음정에 대한 피드백 (현재 상태 + 강점 + 개선 포인트 + 바로 해볼 수 있는 연습 제안)",
  "rhythm": "리듬에 대한 피드백 (현재 상태 + 강점 + 개선 포인트 + 연습 제안)",
  "energy": "에너지에 대한 피드백 (현재 상태 + 강점 + 개선 포인트 + 연습 제안)",
  "emotion": "감정 표현에 대한 피드백 (현재 상태 + 강점 + 개선 포인트 + 연습 제안)",
  "tone": "톤에 대한 피드백 (현재 상태 + 강점 + 개선 포인트 + 연습 제안)",
  "summary": "전체 종합 평가 (3~4문장, 지금 상태 + 앞으로의 방향을 한 번에 정리)",
  "coachComment": "보컬/연주/작곡에 대해 구체적으로 짚어주는 따뜻한 코멘트 (3~4문장)",
  "nextStep": "다음 연습에서 시도해 볼 구체적인 액션 1~2개를 제안하는 문장"
}`;

    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content:
              "당신은 20년 경력의 친절하고 전문적인 음악 코치입니다. ChatGPT처럼 상세하고 구체적이며 친근한 피드백을 제공합니다. 각 항목마다 충분히 길고 도움이 되는 설명을 작성합니다.",
          },
          {
            role: "user",
            content: prompt,
          },
        ],
        temperature: 0.8,
        max_tokens: 2500,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API 오류: ${response.statusText}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content;

    // JSON 파싱
    const comments = JSON.parse(content);
    return comments;
    */
  } catch (error) {
    console.error("❌ AI 코멘트 생성 실패:", error);
    return generateDefaultComments(scores);
  }
}

async function generateClaudeComments(
  scores: AnalysisScores
): Promise<AIComments> {
  const apiKey = process.env.ANTHROPIC_API_KEY;

  if (!apiKey) {
    console.warn(
      "⚠️ Claude API 키가 없습니다. 기본 코멘트를 사용합니다. (ANTHROPIC_API_KEY 필요)"
    );
    return generateDefaultComments(scores);
  }

  const typeLabel =
    scores.analysisType === "vocal"
      ? "보컬"
      : scores.analysisType === "instrument"
        ? "연주"
        : "작곡";

  const prompt = `당신은 20년 경력의 친절한 보컬/음악 코치입니다. 아래 ${typeLabel} 분석 결과를 바탕으로, 사용자가 상처받지 않으면서도 "다음에 뭘 연습해야 할지"가 명확해지도록 피드백을 작성해주세요.

중요:
- 자신이 AI라는 언급, "저는 ~입니다", "ChatGPT", "모델" 같은 표현은 절대 쓰지 마세요.
- 말투는 실제 보컬 트레이너가 옆에서 말하듯 자연스럽고 단호한 존댓말.
- 각 항목은 2~4문장 정도로 짧고 구체적으로.
- 각 항목의 마지막 문장은 "사용자가 답변할 수 있는 질문" 형태로, 대화를 이어가고 싶게 만드는 문장으로 작성하세요.
- 아래 점수들 중에서 "가장 낮은 한 가지 항목"을 오늘의 핵심 포인트로 잡고, 전체 피드백의 비중을 그쪽에 조금 더 실어주세요.

분석 결과 (${typeLabel}):
- 음정 정확도: ${scores.pitchAccuracy}/100
- 리듬 안정성: ${scores.rhythmStability}/100
- 에너지 밸런스: ${scores.energyDynamics}/100
- 감정 표현력: ${scores.emotionalExpression}/100
- 톤 일관성: ${scores.toneConsistency}/100
- 템포: ${scores.tempo} BPM
- 길이: ${Math.round(scores.duration)}초

각 항목에 대해 아래 원칙을 지켜서 작성하세요:
1. 현재 상태를 짧게 설명 (1문장)
2. 강점 1개와 개선 포인트 1개를 짚기 (각 1문장)
3. 바로 연습에 써먹을 수 있는 액션을 제안 (1문장)
4. 과한 감탄사/비유/이모지는 쓰지 말 것

${typeLabel === "보컬" ? "보컬리스트" : typeLabel === "연주" ? "연주자" : "작곡가"}의 관점에서 전문적이면서도 차분한 톤으로 작성해주세요.

추가로, 다음 항목도 작성해주세요:
- coachComment: 따뜻하지만 과하지 않은 코멘트 (3~4문장, 실력과 가능성을 구체적으로 짚어주기)
- nextStep: 다음 단계로 추천하는 연습 방향 (1~2문장, 구체적인 행동 제안)

또한 전체적으로, 사용자가 계속 이야기하고 싶게 만드는 것이 목표입니다. 
- 요약(summary)과 coachComment, nextStep 안에는 "어떤 부분이 제일 신경 쓰였는지", "다음에 어떤 곡이나 부분을 같이 봤으면 좋을지" 등을 물어보는 자연스러운 질문을 꼭 포함하세요.
- 만약 길이가 15초 미만이거나, 점수들이 모두 비슷한 중간 구간(65~80점)에 몰려 있다면, "대략적인 방향을 잡아주는 분석"이라는 점을 한 문장 정도로 언급해주세요.

JSON 형식으로만 응답:
{
  "pitch": "음정에 대한 피드백 (현재 상태 + 강점 + 개선 포인트 + 바로 해볼 수 있는 연습 제안)",
  "rhythm": "리듬에 대한 피드백 (현재 상태 + 강점 + 개선 포인트 + 연습 제안)",
  "energy": "에너지에 대한 피드백 (현재 상태 + 강점 + 개선 포인트 + 연습 제안)",
  "emotion": "감정 표현에 대한 피드백 (현재 상태 + 강점 + 개선 포인트 + 연습 제안)",
  "tone": "톤에 대한 피드백 (현재 상태 + 강점 + 개선 포인트 + 연습 제안)",
  "summary": "전체 종합 평가 (3~4문장, 지금 상태 + 앞으로의 방향을 한 번에 정리)",
  "coachComment": "보컬/연주/작곡에 대해 구체적으로 짚어주는 따뜻한 코멘트 (3~4문장)",
  "nextStep": "다음 연습에서 시도해 볼 구체적인 액션 1~2개를 제안하는 문장"
}`;

  try {
    const response = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
      },
      body: JSON.stringify({
        model: "claude-3-5-sonnet-latest",
        max_tokens: 2500,
        temperature: 0.7,
        messages: [
          {
            role: "user",
            content: prompt,
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`Claude API 오류: ${response.statusText}`);
    }

    const data = await response.json();
    const content =
      data.content &&
      Array.isArray(data.content) &&
      data.content[0] &&
      typeof data.content[0].text === "string"
        ? data.content[0].text
        : "";

    const comments = JSON.parse(content);
    return comments;
  } catch (error) {
    console.error("❌ Claude 코멘트 생성 실패, 기본 코멘트로 폴백:", error);
    return generateDefaultComments(scores);
  }
}

/**
 * 기본 코멘트 생성 (OpenAI 없을 때) - 역할별 감성 피드백
 */
function generateDefaultComments(scores: AnalysisScores): AIComments {
  const type = scores.analysisType || "vocal";
  const typeLabel =
    type === "vocal" ? "보컬" : type === "instrument" ? "연주" : "작곡";

  // 역할별 감성 코멘트
  let coachComment = "";
  let nextStep = "";

  if (type === "vocal") {
    coachComment =
      scores.overallScore >= 85
        ? "당신의 목소리는 따뜻한 봄 햇살처럼 청자의 마음을 부드럽게 감싸 안네요 🌸 음정의 안정성과 감정 전달력이 모두 뛰어나며, 프로페셔널한 보컬리스트의 기본기가 확실히 다져져 있습니다. 특히 고음역대에서의 컨트롤과 저음역대의 풍부한 울림이 인상적이에요. 현재 수준을 유지하면서 더욱 다양한 표현 기법을 시도해보신다면, 더욱 독창적인 보컬리스트로 성장하실 수 있을 거예요!"
        : scores.overallScore >= 70
          ? "당신의 노래는 잔잔한 가을 오후 같은 여운을 남기며 듣는 이의 마음을 편안하게 만들어줍니다 🍂 전반적인 음정과 감정 표현이 안정적이고, 자신만의 색깔을 가지고 있어요. 몇 가지 기술적인 부분만 보완하면 더욱 완성도 높은 보컬을 들려주실 수 있을 것 같아요. 특히 호흡 컨트롤과 고음역대의 안정성을 조금만 더 신경 쓰신다면 완벽에 가까워질 거예요. 이미 충분히 매력적인 목소리를 가지고 계시니 자신감을 가지세요!"
          : "당신의 목소리에는 아직 다듬어지지 않은 원석과 같은 매력이 있어요 🎨 고유한 음색과 개성이 느껴지며, 발전 가능성이 무궁무진합니다. 지금은 기본기를 탄탄히 다지는 시기라고 생각하시면 좋을 것 같아요. 발성 연습과 음정 트레이닝을 꾸준히 하신다면 빠른 시간 내에 눈에 띄는 발전을 경험하실 수 있을 거예요. 음악은 재능도 중요하지만 꾸준한 연습이 더 중요하답니다. 포기하지 말고 계속 도전해보세요!";
    nextStep =
      scores.pitchAccuracy < 85
        ? "매일 10-15분씩 스케일 연습을 하시면서 발성 트레이닝에 집중해보세요 🎤 특히 자신이 어려워하는 음역대를 파악하고, 그 부분을 중점적으로 연습하는 것이 효과적입니다. 튜너 앱을 활용해서 음정 정확도를 체크하면서 연습하시면 더욱 빠른 개선을 경험하실 수 있어요. 호흡 연습도 함께 병행하시면 음정 안정성이 크게 향상될 거예요!"
        : "감정 표현의 디테일을 더 살리는 연습을 추천드립니다 ✨ 같은 곡을 다양한 감정으로 불러보거나, 가사의 의미를 깊이 있게 분석한 후 그 느낌을 목소리에 담아보세요. 프로 보컬리스트들의 라이브 영상을 보면서 미세한 표현 기법들을 관찰하고 따라해보는 것도 큰 도움이 됩니다. 당신의 기술적 완성도에 감정적 깊이가 더해진다면 정말 특별한 보컬리스트가 되실 거예요!";
  } else if (type === "instrument") {
    coachComment =
      scores.overallScore >= 85
        ? "당신의 연주는 여름밤의 별빛처럼 섬세하고 아름다우며, 각 음표 하나하나에 생명력이 느껴집니다 ✨ 터치의 섬세함과 다이내믹의 조절이 매우 세련되었고, 연주자로서의 성숙함이 곳곳에서 드러납니다. 특히 빠른 패시지에서의 정확성과 느린 부분에서의 표현력 모두 뛰어나 균형잡힌 연주를 보여주고 계세요. 이 수준을 유지하시면서 더욱 도전적인 레퍼토리에 도전해보신다면, 한 단계 더 성장하실 수 있을 거예요!"
        : scores.overallScore >= 70
          ? "당신의 연주는 부드러운 겨울 눈송이처럼 따뜻한 감성을 담고 있으며, 듣는 이의 마음을 편안하게 만들어줍니다 ❄️ 전반적인 기술적 완성도가 좋고, 음악적 해석도 안정적이에요. 리듬의 안정성과 터치의 균일성만 조금 더 보완하신다면 완성도가 훨씬 높아질 것 같아요. 메트로놈과 함께 연습하시면서 템포 감각을 더욱 정교하게 다듬어보세요. 이미 충분히 좋은 연주를 하고 계시니, 자신감을 가지고 더 많은 무대 경험을 쌓아보시는 것을 추천드립니다!"
          : "당신의 연주에는 진정성과 열정이 느껴지며, 음악을 진심으로 사랑하는 마음이 전해집니다 🎼 아직은 기술적으로 다듬어야 할 부분들이 있지만, 이는 충분히 연습으로 극복할 수 있는 부분입니다. 천천히 정확하게 연습하는 것부터 시작해서 점차 속도를 높여가는 방식으로 훈련하시면 좋을 것 같아요. 기본 스케일과 아르페지오 연습을 매일 꾸준히 하시면 손가락의 독립성과 정확성이 크게 향상될 거예요. 포기하지 말고 꾸준히 연습하세요!";
    nextStep =
      scores.rhythmStability < 85
        ? "메트로놈을 활용한 리듬 트레이닝을 매일 20-30분씩 해보세요 🎵 처음에는 느린 템포로 시작해서 완벽하게 맞춘 후 점차 속도를 높여가는 것이 중요합니다. 복잡한 리듬 패턴은 손뼉으로 먼저 쳐보고, 그 느낌을 악기로 옮기는 연습을 해보세요. 녹음을 해서 자신의 연주를 객관적으로 들어보는 것도 리듬 감각 향상에 큰 도움이 됩니다. 꾸준히 연습하시면 템포 감각이 자연스럽게 몸에 배게 될 거예요!"
        : "다이내믹 표현의 폭을 넓히는 연습에 집중해보세요 🎹 피아니시모부터 포르티시모까지 다양한 강약을 연습하면서 각 다이내믹에서의 음색 변화를 세밀하게 컨트롤해보세요. 같은 악구를 여러 다이내믹으로 반복 연습하면서 표현의 다양성을 키워보시면 좋습니다. 감정적인 표현력도 함께 향상시키기 위해 곡의 배경이나 이야기를 공부하고, 그 느낌을 연주에 담아보세요. 이미 기술적으로 뛰어나시니 감정적 깊이만 더해진다면 정말 특별한 연주자가 되실 거예요!";
  } else {
    // composition
    coachComment =
      scores.overallScore >= 85
        ? "이 곡은 차분한 여름 저녁 바다처럼 깊은 감정의 레이어를 담고 있으며, 멜로디와 화성의 조화가 매우 세련되고 성숙합니다 🌊 곡의 구조가 탄탄하고, 전개 방식이 자연스러우며, 청자를 몰입시키는 힘이 있어요. 특히 코드 진행의 독창성과 멜로디 라인의 아름다움이 인상적입니다. 편곡의 밸런스도 좋고, 각 악기의 역할 배분이 명확해요. 이런 완성도 높은 작곡 실력을 바탕으로 더욱 다양한 장르와 스타일에 도전해보시면, 더욱 풍부한 음악적 표현력을 갖추게 되실 거예요!"
        : scores.overallScore >= 70
          ? "이 곡은 봄날의 소나기처럼 신선하고 감성적인 느낌을 주며, 작곡가만의 독특한 색깔이 느껴집니다 🌧️ 전반적인 멜로디와 화성이 매력적이고, 곡의 흐름도 자연스러워요. 몇 가지 구조적인 부분만 보완하신다면 훨씬 더 완성도 높은 곡이 될 것 같아요. 특히 클라이맥스 부분의 전개나 브릿지 섹션의 변화를 조금 더 극적으로 만들어보시면 좋을 것 같습니다. 이미 좋은 음악적 아이디어를 가지고 계시니, 편곡과 믹싱에도 더 신경 쓰신다면 프로 수준의 곡을 완성하실 수 있을 거예요!"
          : "이 곡에는 아직 다듬어지지 않았지만 매력적인 아이디어들이 담겨 있어요 🎵 당신만의 독특한 음악적 색깔과 독창성이 느껴집니다. 지금은 기본적인 작곡 이론과 편곡 기법을 익히는 단계라고 생각하시면 좋을 것 같아요. 코드 진행, 멜로디 작법, 곡 구조 등의 기초를 탄탄히 다지시면서 다양한 곡들을 분석해보세요. 좋아하는 아티스트의 곡을 카피해보거나, 악보를 보면서 어떤 구조로 되어 있는지 공부하는 것도 큰 도움이 됩니다. 꾸준히 작곡하고 피드백을 받으시면 빠르게 성장하실 수 있을 거예요!";
    nextStep =
      scores.emotionalExpression < 85
        ? "감정 흐름을 더 자연스럽게 연결하는 코드 진행 연구에 집중해보세요 🎼 같은 멜로디도 코드에 따라 전혀 다른 느낌을 줄 수 있어요. 다양한 보이싱과 텐션 노트를 실험해보면서 자신만의 화성 색깔을 찾아보세요. 또한 곡의 구간별로 감정의 기복을 계획하고, 그에 맞는 편곡 요소들을 배치해보시면 좋습니다. 레퍼런스 트랙을 정해서 분석하고, 어떻게 감정을 전달하는지 세밀하게 관찰해보세요. 이론과 감성의 균형이 맞춰지면 정말 감동적인 곡을 쓰실 수 있을 거예요!"
        : "곡의 클라이맥스 구조를 더 강화하는 작곡 기법을 연구해보세요 ✨ 빌드업과 드롭, 브릿지의 활용 등으로 곡에 긴장감과 해소를 효과적으로 표현해보시면 좋습니다. 편곡에서도 악기의 등장과 퇴장, 레이어링을 전략적으로 사용해서 곡의 다이내믹을 극대화해보세요. 또한 프로 프로듀서들의 작업 과정 영상이나 마스터클래스를 보면서 새로운 아이디어와 기법들을 배워보시는 것을 추천드립니다. 이미 훌륭한 기초를 가지고 계시니, 더욱 과감하고 창의적인 시도를 해보세요!";
  }

  return {
    pitch:
      scores.pitchAccuracy >= 85
        ? `${scores.pitchAccuracy}점 - 음정 정확도가 매우 뛰어나며 전체적으로 안정적입니다. ${type === "vocal" ? "특히 고음역대와 저음역대 모두에서 정확한 음정 유지력을 보여주고 있으며, 후렴 부분에서의 탁월한 컨트롤이 인상적입니다. 피치 플럭스가 거의 없어 전문 보컬리스트 수준의 음정 안정성을 갖추고 계세요. 이 수준을 유지하면서 더욱 복잡한 멜로디 라인에 도전해보시면 좋을 것 같습니다." : type === "instrument" ? "화성 구조가 매우 정확하며 각 음의 튜닝이 완벽합니다. 코드 보이싱도 깔끔하고, 불협화음 없이 아름다운 하모니를 만들어내고 있어요. 이런 기초가 탄탄하시니 더욱 복잡한 재즈 코드나 텐션 노트를 활용한 진행에 도전해보세요." : "멜로디와 화성의 구조가 매우 탄탄하며, 각 음의 배치가 이론적으로 정확합니다. 코드 진행이 자연스럽고, 전조나 차용화음의 사용도 적절해요. 이런 완성도 높은 화성 감각을 바탕으로 더욱 실험적인 화성 진행에 도전해보시면 좋겠습니다."}`
        : scores.pitchAccuracy >= 70
          ? `${scores.pitchAccuracy}점 - 전반적으로 음정이 안정적이나 개선의 여지가 있습니다. ${type === "vocal" ? "대부분의 구간에서 정확한 음정을 유지하고 있지만, 고음 부분에서 약간의 흔들림이 감지됩니다. 이는 호흡 지지력과 관련이 있을 수 있으니, 복식호흡 연습과 함께 고음역대 발성 연습을 꾸준히 해보세요. 또한 피치 모니터링 앱을 활용해서 실시간으로 자신의 음정을 체크하면서 연습하시면 빠른 개선을 경험하실 수 있을 거예요." : type === "instrument" ? "음정이 대체로 안정적이지만 일부 구간에서 미세한 튜닝 이슈가 있습니다. 특히 빠른 패시지나 포지션 이동 시 음정이 약간 흔들리는 경향이 있어요. 천천히 정확하게 연습하면서 각 음의 정확한 위치를 손가락에 익히는 연습을 해보세요. 튜너를 켜놓고 연습하시면 음정 감각이 더욱 정교해질 거예요." : "멜로디 라인이 매력적이지만 일부 화성에서 불안정함이 느껴집니다. 코드 진행이 자연스럽지만, 몇몇 음들이 조성에서 약간 벗어나는 느낌이 있어요. 기본 화성학을 복습하면서 각 코드의 구성음을 정확히 파악하는 연습을 해보시면 좋겠습니다. 레퍼런스 트랙의 코드 진행을 분석해보는 것도 큰 도움이 될 거예요."}`
          : `${scores.pitchAccuracy}점 - 음정 정확도 향상이 필요한 단계입니다. ${type === "vocal" ? "현재 음정의 불안정성이 다소 있어 기본적인 음정 트레이닝에 집중할 필요가 있습니다. 하지만 이는 충분히 연습으로 극복할 수 있는 부분이니 너무 걱정하지 마세요. 매일 스케일 연습을 10-15분씩 하시고, 음정 트레이닝 앱(예: Vocal Pitch Monitor)을 활용해서 시각적 피드백을 받으며 연습해보세요. 처음에는 느린 템포로 정확하게, 그다음 점차 속도를 높여가는 방식으로 훈련하시면 빠르게 개선될 거예요." : type === "instrument" ? "음정 정확도를 높이기 위한 기본 연습이 필요합니다. 악기 튜닝을 정확히 하고, 기본 스케일과 아르페지오 연습을 매일 꾸준히 해보세요. 천천히 정확하게 연습하는 것이 빠르고 부정확하게 연습하는 것보다 훨씬 효과적입니다. 튜너와 메트로놈을 활용해서 객관적인 피드백을 받으며 연습하시면 음정 감각이 빠르게 향상될 거예요. 포기하지 마세요!" : "화성과 멜로디의 기본 구조를 다시 점검해볼 필요가 있습니다. 기본 코드 이론과 스케일을 복습하면서 각 조성의 다이아토닉 코드를 숙지해보세요. 좋아하는 곡의 코드 진행을 카피하면서 왜 그런 코드가 사용되었는지 분석해보는 것도 큰 도움이 됩니다. 이론과 실습을 병행하시면 빠르게 성장하실 수 있을 거예요."}`,
    rhythm:
      scores.rhythmStability >= 85
        ? `${scores.rhythmStability}점 - 리듬 안정성이 매우 뛰어나며 템포 감각이 정확합니다. 전체적으로 일정한 템포를 유지하면서도 음악적 표현을 위한 자연스러운 루바토가 적절히 사용되고 있어요. 복잡한 리듬 패턴도 정확하게 소화하고 있으며, 그루브 감각이 탁월합니다. 이런 리듬 감각은 오랜 훈련을 통해 얻어지는 것인데, 이미 그 경지에 도달하셨네요. 이 수준을 유지하시면서 더욱 다양한 리듬 스타일(스윙, 셔플, 폴리리듬 등)을 경험해보시면 좋겠습니다.`
        : scores.rhythmStability >= 70
          ? `${scores.rhythmStability}점 - 리듬이 전반적으로 안정적이지만 일부 구간에서 흔들림이 있습니다. 대부분의 부분에서 템포를 잘 유지하고 있지만, 빠른 구간이나 복잡한 리듬 패턴에서 약간 서두르거나 늦어지는 경향이 있어요. 이는 메트로놈과 함께 연습하면서 내면의 템포 감각을 키우면 쉽게 개선될 수 있습니다. 처음에는 느린 템포로 완벽하게 맞춰보고, 점차 원래 템포로 높여가는 연습을 해보세요. 자신의 연주를 녹음해서 들어보면 어느 부분에서 템포가 흔들리는지 객관적으로 파악할 수 있어요.`
          : `${scores.rhythmStability}점 - 리듬 안정성 향상이 필요한 단계입니다. 현재 템포 유지에 어려움이 있어 보이지만, 이는 체계적인 리듬 훈련으로 충분히 개선할 수 있는 부분입니다. 매일 메트로놈과 함께 기본 리듬 패턴 연습을 20-30분씩 해보세요. 4분음표, 8분음표, 16분음표 등 기본 박자부터 정확하게 연습하고, 점차 복잡한 리듬으로 확장해가는 것이 효과적입니다. 손뼉으로 리듬을 치면서 몸으로 템포를 느끼는 연습도 큰 도움이 됩니다. 꾸준히 연습하시면 리듬 감각이 자연스럽게 몸에 배게 될 거예요.`,
    energy:
      scores.energyDynamics >= 85
        ? `${scores.energyDynamics}점 - 에너지 밸런스와 다이내믹 컨트롤이 매우 훌륭합니다. 곡의 흐름에 맞춰 자연스럽게 강약을 조절하고 있으며, 클라이맥스에서의 에너지 분출과 조용한 부분에서의 절제가 모두 뛰어납니다. 다이내믹 레인지가 넓어 표현의 폭이 다양하고, 청자를 지루하지 않게 만드는 힘이 있어요. 이런 에너지 컨트롤은 음악의 이야기를 효과적으로 전달하는 핵심 요소인데, 이미 완벽하게 구사하고 계시네요. 이 감각을 유지하시면서 더욱 극적인 표현에 도전해보시면 좋겠습니다.`
        : scores.energyDynamics >= 70
          ? `${scores.energyDynamics}점 - 에너지 레벨이 전반적으로 적절하나 더 극적인 표현이 가능합니다. 다이내믹의 기본은 갖추고 있지만, 클라이맥스 부분에서 더 강한 에너지를 내거나 조용한 부분을 더욱 섬세하게 표현하면 곡의 드라마가 더욱 살아날 것 같아요. 피아니시모에서 포르티시모까지의 다이내믹 레인지를 더 넓게 활용해보세요. 같은 구절을 여러 다이내믹으로 반복 연습하면서 각 강약에서의 음색 변화를 세밀하게 컨트롤하는 연습을 해보시면 좋습니다. 에너지의 변화가 곡에 생명력을 불어넣어줄 거예요.`
          : `${scores.energyDynamics}점 - 에너지 변화를 더 크게 주는 연습이 필요합니다. 현재는 다이내믹의 변화가 다소 평면적이어서 곡의 기복이 잘 느껴지지 않아요. 하지만 이는 의식적인 연습으로 충분히 개선할 수 있습니다. 악보에 다이내믹 마킹(p, mf, f, ff 등)을 명확히 표시하고, 각 부분에서 의도한 강약을 과장되게 표현하는 연습부터 시작해보세요. 처음에는 과하다 싶을 정도로 크게 표현하다 보면 점차 적절한 수준을 찾게 됩니다. 곡의 감정 곡선을 미리 그려보고, 그에 맞춰 에너지를 분배하는 연습도 효과적입니다.`,
    emotion:
      scores.emotionalExpression >= 85
        ? `${scores.emotionalExpression}점 - 감정 표현력이 매우 풍부하며 깊은 몰입감을 전달합니다. 음악에 담긴 감정을 청자에게 효과적으로 전달하고 있으며, 기술적 완성도와 감성적 깊이가 완벽하게 조화를 이루고 있어요. 각 프레이즈마다 살아있는 감정이 느껴지고, 청자를 음악의 세계로 끌어들이는 힘이 있습니다. 이런 감정 표현력은 단순히 기술만으로는 만들어질 수 없고, 음악에 대한 깊은 이해와 진정성에서 나오는 것인데, 그 경지에 이미 도달하셨네요. 현재의 감성을 유지하시면서 더욱 다양한 감정적 스펙트럼을 탐험해보시면 좋겠습니다.`
        : scores.emotionalExpression >= 70
          ? `${scores.emotionalExpression}점 - 감정 표현이 안정적이며 진정성이 느껴집니다. 음악에 담긴 감정을 어느 정도 잘 전달하고 있지만, 더 과감하고 다양한 표현을 시도해보시면 좋을 것 같아요. 때로는 절제된 표현도 좋지만, 감정을 더 극적으로 드러내는 것도 필요합니다. 가사의 의미를 더 깊이 분석해보거나, 곡이 전달하고자 하는 이야기를 상상하면서 연주해보세요. 또한 다양한 아티스트들의 해석을 들어보면서 같은 곡도 얼마나 다르게 표현될 수 있는지 관찰해보는 것도 도움이 됩니다. 기술적 완성도에 감정적 깊이가 더해지면 정말 특별한 연주가 될 거예요.`
          : `${scores.emotionalExpression}점 - 감정 표현을 더 풍부하게 만드는 연습이 필요합니다. 현재는 기술적인 부분에 집중하느라 감정 표현까지 신경 쓰기 어려울 수 있어요. 하지만 음악의 진정한 매력은 감정 전달에 있습니다. 먼저 곡의 가사나 배경 스토리를 깊이 이해하고, 그 느낌을 자신의 것으로 만드는 연습을 해보세요. 거울을 보면서 표정 연기를 하듯이 연주하거나, 곡의 감정을 말로 설명해보는 것도 도움이 됩니다. 기술이 어느 정도 익숙해지면 자연스럽게 감정 표현에 더 집중할 수 있게 될 거예요. 지금은 기초를 다지는 시기이니 너무 조급해하지 마세요.`,
    tone:
      scores.toneConsistency >= 85
        ? `${scores.toneConsistency}점 - 톤의 일관성과 음색이 매우 뛰어나며 아름답습니다. 전체적으로 균일하고 안정적인 음색을 유지하고 있으며, 각 음역대에서의 음색 변화도 자연스럽고 조화롭습니다. 특히 ${type === "vocal" ? "목소리의 울림과 공명이 풍부하며, 고음역대와 저음역대 모두에서 아름다운 음색을 들려주고 있어요. 성대의 긴장 없이 자연스러운 발성을 하고 계시며, 이는 오랜 훈련의 결과입니다." : "악기의 음색이 고르고 일관적이며, 각 음에서 명확하고 깨끗한 소리를 내고 있어요. 터치의 균일성이 뛰어나고, 음색 컨트롤이 정교합니다."} 이런 아름다운 톤은 청자에게 큰 감동을 주는 요소입니다. 현재의 음색을 유지하시면서 더욱 다양한 음색 팔레트를 개발해보시면 좋겠습니다.`
        : scores.toneConsistency >= 70
          ? `${scores.toneConsistency}점 - 톤이 전반적으로 안정적이나 일부 구간에서 개선이 필요합니다. ${type === "vocal" ? "대부분의 음역대에서 좋은 음색을 들려주고 있지만, 고음역대에서 약간 긴장되거나 날카로워지는 경향이 있어요. 이는 호흡 지지력과 후두의 위치와 관련이 있을 수 있습니다. 고음을 낼 때 목에 힘을 빼고 복식호흡으로 지지하는 연습을 해보세요. 또한 성대를 이완시키는 발성 연습(립트릴, 허밍 등)도 도움이 됩니다." : "악기의 음색이 대체로 좋지만 일부 구간에서 밸런스가 필요합니다. 특히 강하게 연주할 때 음색이 거칠어지거나, 약하게 연주할 때 소리가 빈약해지는 경향이 있어요. 다양한 다이내믹에서도 일관된 음색을 유지하는 연습을 해보세요."} 톤의 일관성은 많은 연습으로 개선될 수 있으니 꾸준히 노력해보세요.`
          : `${scores.toneConsistency}점 - 톤의 일관성 향상이 필요한 단계입니다. ${type === "vocal" ? "현재 음색이 불안정하거나 각 음역대에서 차이가 크게 나타나고 있어요. 이는 발성 방법의 문제일 수 있으니, 기본적인 발성 교정이 필요합니다. 전문 보컬 코치의 도움을 받으시면 더 빠르게 개선될 수 있습니다. 매일 발성 연습을 하면서 편안하고 자연스러운 소리를 내는 연습을 해보세요. 거울을 보면서 턱과 목의 긴장을 풀고, 복식호흡으로 지지하는 것이 중요합니다." : "악기의 음색 밸런스를 잡는 연습이 필요합니다. 기본적인 터치 컨트롤과 음색 만들기 연습을 꾸준히 해보세요. 롱톤 연습을 통해 각 음에서 일관된 소리를 내는 훈련을 하시면 좋습니다. 전문 연주자의 연주를 들으면서 좋은 음색이 무엇인지 귀로 익히는 것도 중요합니다."} 톤은 연주자의 시그니처이니 꾸준히 발전시켜보세요.`,
    summary:
      scores.overallScore >= 85
        ? `지금 노래는 전체적으로 완성도가 높고, 이미 무대에 올려도 될 정도의 기본기를 갖추고 있어요. 특히 ${scores.pitchAccuracy >= 90 ? "음정 정확도가 거의 틀어지지 않는 점" : scores.rhythmStability >= 90 ? "리듬을 끝까지 안정적으로 끌고 가는 힘" : scores.emotionalExpression >= 90 ? "가사 느낌을 자연스럽게 살려내는 감정 표현" : "여러 요소가 고르게 잘 잡혀 있는 균형감"}이 눈에 띕니다. 이 정도면 “잘하고 있다”가 기준선이라, 이제는 곡 선택이나 표현 방식처럼 본인 색깔을 더 드러내는 쪽으로 방향을 잡아도 좋아요. 앞으로는 어떤 곡에서, 어떤 분위기를 더 밀어보고 싶은지 나랑 같이 정해볼까요?`
        : scores.overallScore >= 70
          ? `지금 노래는 전반적으로 안정적이고, ${typeLabel}로서 기본기는 꽤 잘 잡혀 있는 편이에요. 특히 ${scores.pitchAccuracy >= 75 ? "음정이 크게 흔들리지 않는 점" : scores.rhythmStability >= 75 ? "박자를 크게 놓치지 않고 따라가는 리듬 감각" : scores.emotionalExpression >= 75 ? "곡 분위기를 대체로 잘 따라가는 감정 표현" : "크게 무너지지 않는 전반적인 밸런스"}이 장점이라, 이 느낌만 유지해도 들을 때 불편함은 거의 없을 거예요. 다만 ${scores.pitchAccuracy < 75 ? "음정" : scores.rhythmStability < 75 ? "리듬" : scores.energyDynamics < 75 ? "다이내믹(강약)" : "감정 전달"} 쪽을 조금만 더 신경 써 주면 훨씬 또렷하게 “잘한다”는 인상이 남을 거예요. 다음 녹음에서는 이 한 가지 포인트 중 어떤 걸 먼저 잡아보고 싶은지 말해줄래요?`
          : `지금은 기본기를 다져가는 단계라, 노래를 부를 때 몸과 호흡, 귀가 아직 서로 완전히 맞춰지지 않은 느낌이 있어요. 그렇다고 부족함만 있는 건 아니고, ${scores.pitchAccuracy >= 65 ? "생각보다 음정이 크게 무너지지는 않는 편" : scores.emotionalExpression >= 65 ? "곡을 그냥 읽는 게 아니라 나름대로 느낌을 실으려는 시도" : "목소리 자체에서 나오는 본인만의 색깔"} 같은 좋은 출발점도 분명히 보입니다. 중요한 건 한 번에 다 고치려 하기보다, 한 번 녹음할 때마다 “이번에는 이 한 가지만 잡아보자”라고 목표를 쪼개는 거예요. 지금 불러본 곡에서 본인이 제일 거슬렸던 부분은 어디였는지, 한 번 적어볼까요?`,
    coachComment,
    nextStep,
  };
}

/**
 * 연습 추천 생성 (점수 기반 + 유형별)
 */
export async function generateRecommendations(
  scores: AnalysisScores
): Promise<PracticeRecommendation[]> {
  const recommendations: PracticeRecommendation[] = [];
  const type = scores.analysisType || "vocal";

  // 음정이 낮으면 피치 트레이닝 추천
  if (scores.pitchAccuracy < 85) {
    if (type === "vocal") {
      recommendations.push({
        title: "보컬 피치 교정 훈련",
        description:
          "음정 정확도 향상을 위한 스케일 연습과 튜닝 앱 활용을 추천합니다.",
        link: "/lessons/pitch-training",
        practiceSongs: [
          "Adele - Easy On Me",
          "Ed Sheeran - Perfect",
          "Taylor Swift - Anti-Hero",
        ],
      });
    } else if (type === "instrument") {
      recommendations.push({
        title: "악기 음정 트레이닝",
        description:
          "튜너를 사용한 정확한 음정 연습과 스케일 반복 연습을 추천합니다.",
        link: "/lessons/instrument-pitch",
        practiceSongs: [
          "Canon in D (Pachelbel)",
          "Für Elise (Beethoven)",
          "River Flows in You (Yiruma)",
        ],
      });
    } else {
      recommendations.push({
        title: "화성 구조 학습",
        description:
          "코드 진행의 정확성을 높이기 위한 화성학 기초 학습을 추천합니다.",
        link: "/lessons/harmony-basics",
      });
    }
  }

  // 리듬이 불안정하면 리듬 트레이닝 추천
  if (scores.rhythmStability < 85) {
    if (type === "vocal") {
      recommendations.push({
        title: "보컬 리듬 트레이닝",
        description:
          "메트로놈과 함께 연습하여 템포 감각을 키우는 것이 효과적입니다.",
        link: "/lessons/rhythm-training",
        practiceSongs: [
          "Calvin Harris - Summer",
          "OneRepublic - Counting Stars",
          "Bruno Mars - Uptown Funk",
        ],
      });
    } else if (type === "instrument") {
      recommendations.push({
        title: "악기 리듬 연습",
        description:
          "복잡한 리듬 패턴 연습과 메트로놈을 활용한 템포 안정화 훈련을 추천합니다.",
        link: "/lessons/instrument-rhythm",
        practiceSongs: [
          "Clocks (Coldplay)",
          "Bohemian Rhapsody (Queen)",
          "Seven Nation Army (White Stripes)",
        ],
      });
    } else {
      recommendations.push({
        title: "리듬 구조 강화",
        description:
          "드럼 패턴 연구와 그루브 분석을 통해 리듬 구조를 강화해보세요.",
        link: "/lessons/rhythm-structure",
      });
    }
  }

  // 에너지가 낮으면 다이내믹 연습 추천
  if (scores.energyDynamics < 85) {
    recommendations.push({
      title: "다이내믹 표현 연습",
      description:
        "강약 조절을 통해 곡의 감정을 더 풍부하게 표현하는 연습이 필요합니다.",
      link: "/lessons/dynamics",
      practiceSongs: [
        "Queen - Bohemian Rhapsody",
        "Whitney Houston - I Will Always Love You",
        "Beyoncé - Halo",
      ],
    });
  }

  // 감정 표현이 약하면 표현력 향상 추천
  if (scores.emotionalExpression < 85) {
    recommendations.push({
      title: "감정 표현 확장",
      description:
        "다양한 감정을 표현하는 연습으로 표현력을 확장할 수 있습니다.",
      link: "/lessons/expression",
      practiceSongs: [
        "Adele - Someone Like You",
        "Jeff Buckley - Hallelujah",
        "Coldplay - Fix You",
      ],
    });
  }

  // 톤이 불안정하면 발성 교정 추천
  if (scores.toneConsistency < 85) {
    recommendations.push({
      title: "발성 교정 및 톤 안정화",
      description:
        "일관된 톤을 유지하기 위한 발성 연습과 호흡 조절이 필요합니다.",
      link: "/lessons/vocal-tone",
      practiceSongs: [
        "The Beatles - Yesterday",
        "Lady Gaga - Shallow",
        "Christina Perri - A Thousand Years",
      ],
    });
  }

  // 모든 점수가 높으면 고급 과정 추천
  if (scores.overallScore >= 85) {
    recommendations.push({
      title: "고난도 곡 도전",
      description:
        "현재 수준이 우수합니다. 더 어려운 곡에 도전하여 실력을 확장해보세요.",
      link: "/lessons/advanced",
      practiceSongs: [
        "The Greatest Showman - Never Enough",
        "Les Misérables - I Dreamed a Dream",
        "Andrew Lloyd Webber - Phantom of the Opera",
      ],
    });
  }

  return recommendations;
}
